import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Search, Filter, ChevronDown, ChevronUp, Eye, Download } from 'lucide-react';
import { fetchCallFilterOptions, fetchCalls, getTaskAudioUrl } from '../api';
import { useExportJobs } from './exports/ExportJobsContext';
import { getTexts } from '../utils/configLoader';
import useEmployees from '../hooks/useEmployees';

const PAGE_SIZE = 25;

const CALL_TYPE_LABELS = {
  order_new: 'Новый заказ',
  order_confirm_outbound: 'Подтверждение заказа',
  order_change: 'Изменение заказа',
  delivery_issue: 'Жалоба',
  consultation: 'Консультация',
  callback_missed: 'Пропущенный дозвон',
  wrong_number: 'Ошибочный номер'
};

const NEGATIVE_LABELS = {
  no: 'Нет',
  mild: 'Умеренный',
  hard: 'Жесткий'
};

const NEGATIVE_BADGE_CLASSES = {
  no: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  mild: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  hard: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
};

const UPSELL_LABELS = {
  successful: 'Успешная',
  attempted: 'Попытка',
  none: 'Без попытки',
};

const UPSELL_BADGE_CLASSES = {
  successful: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
  attempted: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
  none: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const UPSELL_FILTER_OPTIONS = [
  { value: 'all', label: 'Все допродажи' },
  { value: 'successful', label: UPSELL_LABELS.successful },
  { value: 'attempted', label: UPSELL_LABELS.attempted },
  { value: 'none', label: UPSELL_LABELS.none },
];

const CHECKLIST_LABELS = {
  greeting: 'Приветствие',
  order_readback: 'Проговаривание заказа',
  add_ons: 'Уточнение добавок',
  cutlery: 'Уточнение приборов',
  delivery_or_pickup: 'Подтверждение доставки/самовывоза',
  payment_method: 'Способ оплаты',
  payment_amount: 'Сумма оплаты',
  delivery_time: 'Время доставки',
  farewell: 'Прощание'
};

const formatTranscriptLines = (transcript) => {
  if (!transcript) {
    return [];
  }

  const normalized = transcript.replace(/\r\n/g, '\n');
  const speakerChunks = normalized.match(/\[Спикер [AB]\][^\[]*/gi);
  const chunks = speakerChunks && speakerChunks.length ? speakerChunks : normalized.split('\n');

  return chunks
    .map((line) => line.trim())
    .filter(Boolean);
};

const getUpsellStatus = (call) => {
  if (call?.upsellSuccessful === true) return 'successful';
  if (call?.upsellAttempted === true) return 'attempted';
  return 'none';
};

const CallDetails = ({ dateRange, initialCallId, onInitialCallHandled }) => {
  const texts = getTexts('calls');
  const { employees } = useEmployees();
  const { startExport } = useExportJobs();

  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedClassificationType, setSelectedClassificationType] = useState('all');
  const [selectedNegativeSeverity, setSelectedNegativeSeverity] = useState('all');
  const [selectedUpsellStatus, setSelectedUpsellStatus] = useState('all');
  const [selectedCallType, setSelectedCallType] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [selectedCall, setSelectedCall] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [classificationTypeOptions, setClassificationTypeOptions] = useState(['all']);
  const [negativeSeverityOptions, setNegativeSeverityOptions] = useState(['all']);

  const transcriptLines = useMemo(
    () => formatTranscriptLines(selectedCall?.transcript),
    [selectedCall?.transcript]
  );

  const loadCalls = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
        employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
        call_classification_type: selectedClassificationType !== 'all' ? selectedClassificationType : undefined,
        negative_severity: selectedNegativeSeverity !== 'all' ? selectedNegativeSeverity : undefined,
        upsell_status: selectedUpsellStatus !== 'all' ? selectedUpsellStatus : undefined,
        call_type: selectedCallType !== 'all' ? selectedCallType : undefined,
        search: searchQuery || undefined,
        date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
        date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
        sort: sortConfig.key ?? undefined,
        order: sortConfig.key ? sortConfig.direction : undefined
      };

      const response = await fetchCalls(params);
      const items = response.items ?? [];

      setCalls(items);
      setTotal(response.total ?? 0);
    } catch (err) {
      console.error(err);
      setError('Не удалось загрузить список звонков.');
    } finally {
      setIsLoading(false);
    }
  }, [page, selectedDepartment, selectedEmployee, selectedClassificationType, selectedNegativeSeverity, selectedUpsellStatus, selectedCallType, searchQuery, dateRange?.from, dateRange?.to, sortConfig]);

  useEffect(() => {
    setPage(1);
  }, [dateRange?.from, dateRange?.to, selectedClassificationType, selectedNegativeSeverity, selectedUpsellStatus, selectedCallType, selectedDepartment, selectedEmployee]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  useEffect(() => {
    if (!dateRange?.from || !dateRange?.to) {
      setClassificationTypeOptions(['all']);
      setNegativeSeverityOptions(['all']);
      return undefined;
    }

    let cancelled = false;

    const loadFilterOptions = async () => {
      try {
        const response = await fetchCallFilterOptions({
          date_from: format(dateRange.from, 'yyyy-MM-dd'),
          date_to: format(dateRange.to, 'yyyy-MM-dd')
        });

        if (cancelled) {
          return;
        }

        const classificationTypes = ['all', ...(response.classificationTypes ?? [])];
        const negativeSeverities = ['all', ...(response.negativeSeverities ?? [])];

        setClassificationTypeOptions(classificationTypes);
        setNegativeSeverityOptions(negativeSeverities);
        setSelectedClassificationType((current) => (
          current === 'all' || classificationTypes.includes(current) ? current : 'all'
        ));
        setSelectedNegativeSeverity((current) => (
          current === 'all' || negativeSeverities.includes(current) ? current : 'all'
        ));
      } catch (err) {
        if (!cancelled) {
          console.error(err);
        }
      }
    };

    loadFilterOptions();

    return () => {
      cancelled = true;
    };
  }, [dateRange?.from, dateRange?.to]);

  useEffect(() => {
    if (!initialCallId) {
      return;
    }

    let cancelled = false;

    fetchCalls({
      call_id: initialCallId,
      limit: 1
    })
      .then((response) => {
        if (cancelled) {
          return;
        }

        const call = response.items?.[0];
        if (call) {
          setSelectedCall(call);
          setShowDetailsModal(true);
          return;
        }

        setError('Звонок по ссылке не найден.');
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          onInitialCallHandled?.();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [initialCallId, onInitialCallHandled]);

  const handleExport = useCallback(() => {
    const params = {
      department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
      employee_id: selectedEmployee !== 'all' ? selectedEmployee : undefined,
      call_classification_type: selectedClassificationType !== 'all' ? selectedClassificationType : undefined,
      negative_severity: selectedNegativeSeverity !== 'all' ? selectedNegativeSeverity : undefined,
      upsell_status: selectedUpsellStatus !== 'all' ? selectedUpsellStatus : undefined,
      call_type: selectedCallType !== 'all' ? selectedCallType : undefined,
      search: searchQuery || undefined,
      date_from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      date_to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    };

    startExport(params);
  }, [dateRange?.from, dateRange?.to, searchQuery, selectedCallType, selectedClassificationType, selectedDepartment, selectedEmployee, selectedNegativeSeverity, selectedUpsellStatus, startExport]);

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.key === column) {
        return {
          key: column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }

      return { key: column, direction: 'asc' };
    });
  };

  const sortedCalls = useMemo(() => {
    if (!sortConfig.key) {
      return calls;
    }

    const key = sortConfig.key;
    const direction = sortConfig.direction === 'asc' ? 1 : -1;

    return [...calls].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
  }, [calls, sortConfig]);

  const departmentOptions = useMemo(() => [
    { id: 'all', name: 'Все отделы' },
    ...[...new Set(employees.filter((employee) => employee.department).map((employee) => employee.department))]
      .sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' }))
      .map((department) => ({ id: department, name: department }))
  ], [employees]);

  const uniqueEmployees = useMemo(() => {
    const filteredEmployees = selectedDepartment === 'all'
      ? employees
      : employees.filter((employee) => employee.department === selectedDepartment);

    return [
      { id: 'all', name: 'Все сотрудники' },
      ...filteredEmployees.map((emp) => ({
        id: emp.id?.toString() ?? emp.name,
        name: emp.name ?? emp.fullName ?? 'Без имени'
      }))
    ];
  }, [employees, selectedDepartment]);

  const handleDepartmentChange = useCallback((department) => {
    setSelectedDepartment(department);
    setSelectedEmployee('all');
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="space-y-3">
          <div className="text-left">
            <CardTitle>{texts.title}</CardTitle>
            <CardDescription>{texts.description}</CardDescription>
          </div>
          <div className="grid w-full gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-9">
            <div className="relative w-full">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={texts.filters.search}
                value={searchQuery}
                onChange={(event) => {
                  setPage(1);
                  setSearchQuery(event.target.value);
                }}
              />
            </div>

            <Select value={selectedDepartment} onValueChange={handleDepartmentChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Все отделы" />
              </SelectTrigger>
              <SelectContent>
                {departmentOptions.map((department) => (
                  <SelectItem key={department.id} value={department.id}>
                    {department.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Сотрудник" />
              </SelectTrigger>
              <SelectContent>
                {uniqueEmployees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedClassificationType} onValueChange={setSelectedClassificationType}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Тип звонка" />
              </SelectTrigger>
              <SelectContent>
                {classificationTypeOptions.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item === 'all' ? 'Все типы' : CALL_TYPE_LABELS[item] ?? item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedNegativeSeverity} onValueChange={setSelectedNegativeSeverity}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Негатив" />
              </SelectTrigger>
              <SelectContent>
                {negativeSeverityOptions.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item === 'all' ? 'Любой негатив' : NEGATIVE_LABELS[item] ?? item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedCallType} onValueChange={setSelectedCallType}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Направление" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все направления</SelectItem>
                <SelectItem value="incoming">Входящие</SelectItem>
                <SelectItem value="outgoing">Исходящие</SelectItem>
                <SelectItem value="internal">Внутренние</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedUpsellStatus} onValueChange={setSelectedUpsellStatus}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Допродажа" />
              </SelectTrigger>
              <SelectContent>
                {UPSELL_FILTER_OPTIONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" className="w-full" onClick={loadCalls}>
              <Filter className="mr-2 h-4 w-4" />
              Обновить
            </Button>

            <Button variant="outline" className="w-full" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Excel
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <CardTitle>Журнал звонков</CardTitle>
          <div className="text-sm text-muted-foreground">
            Страница {page} из {totalPages} &middot; Всего {total.toLocaleString()}
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead label="Дата" column="callStartedAt" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Сотрудник" column="employeeName" sortConfig={sortConfig} onSort={handleSort} />
                  <TableHead>Клиент</TableHead>
                  <TableHead>Направление</TableHead>
                  <TableHead>Тип</TableHead>
                  <TableHead>Негатив</TableHead>
                  <TableHead>Жалобы</TableHead>
                  <TableHead>Допродажа</TableHead>
                  <SortableHead label="Чек-лист" column="checklistScore" sortConfig={sortConfig} onSort={handleSort} />
                  <SortableHead label="Длительность" column="durationSeconds" sortConfig={sortConfig} onSort={handleSort} />
                  <TableHead>Подробнее</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground">
                      Загружаем данные...
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && sortedCalls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground">
                      Нет записей для отображения.
                    </TableCell>
                  </TableRow>
                )}
                {sortedCalls.map((call) => (
                  <TableRow key={call.callId} className="[&>td]:align-middle [&>td]:py-3">
                    <TableCell>{formatDate(call.callStartedAt)}</TableCell>
                    <TableCell>{call.employeeName || '—'}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{call.clientName || '—'}</div>
                      <div className="text-xs text-muted-foreground">{call.clientCompany}</div>
                    </TableCell>
                    <TableCell>{formatDirection(call.callType)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge variant="outline">{formatClassificationType(call.callClassificationType)}</Badge>
                        {isComplaint(call) && call.callClassificationType !== 'delivery_issue' && (
                          <Badge className={NEGATIVE_BADGE_CLASSES.hard}>Жалоба</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={NEGATIVE_BADGE_CLASSES[call.negativeSeverity] ?? NEGATIVE_BADGE_CLASSES.no}>
                        {NEGATIVE_LABELS[call.negativeSeverity] ?? call.negativeSeverity ?? 'Нет'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatComplaints(call)}</TableCell>
                    <TableCell>
                      {(() => {
                        const status = getUpsellStatus(call);
                        return (
                          <Badge className={UPSELL_BADGE_CLASSES[status]}>
                            {UPSELL_LABELS[status]}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>{formatChecklistScore(call.checklistScore)}</TableCell>
                    <TableCell>{formatDuration(call.durationSeconds)}</TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" onClick={() => { setSelectedCall(call); setShowDetailsModal(true); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Показано {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} из {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Назад
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              >
                Вперёд
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Детали звонка</DialogTitle>
            <DialogDescription>{formatDetailsSubtitle(selectedCall)}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <InfoBlock label="Тип звонка" value={formatClassificationType(selectedCall?.callClassificationType)} />
              <InfoBlock label="Негатив" value={NEGATIVE_LABELS[selectedCall?.negativeSeverity] ?? selectedCall?.negativeSeverity ?? 'Нет'} />
              <InfoBlock label="Допродажа" value={UPSELL_LABELS[getUpsellStatus(selectedCall)]} />
              <InfoBlock label="Чек-лист" value={formatChecklistScore(selectedCall?.checklistScore)} />
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">Чек-лист</h4>
              <div className="rounded-md bg-muted p-3 text-sm leading-relaxed space-y-2">
                {Object.entries(CHECKLIST_LABELS).map(([key, label]) => {
                  const item = selectedCall?.checklist?.[key];
                  const value = item?.value === true;
                  return (
                    <div key={key} className="flex items-center justify-between border-b border-border/50 pb-1 last:border-b-0">
                      <span>{label}</span>
                      <span className={value ? 'text-emerald-600 font-medium' : 'text-red-500 font-medium'}>
                        {value ? '✓' : '✗'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">Жалобы</h4>
              <div className="rounded-md bg-muted p-3 text-sm leading-relaxed">
                {formatComplaints(selectedCall)}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">Расшифровка звонка</h4>
              <div className="rounded-md bg-muted p-3 text-sm leading-relaxed space-y-2">
                {transcriptLines.length
                  ? transcriptLines.map((line, index) => (
                      <div key={index} className="whitespace-pre-line">
                        {line}
                      </div>
                    ))
                  : 'Нет транскрипта'}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-foreground">Прослушать запись</h4>
                {resolveRecordingUrl(selectedCall) && (
                  <Button asChild size="sm" variant="outline">
                    <a href={resolveRecordingUrl(selectedCall)} download>
                      Скачать
                    </a>
                  </Button>
                )}
              </div>
              {resolveRecordingUrl(selectedCall) ? (
                <audio className="w-full" src={resolveRecordingUrl(selectedCall)} controls />
              ) : (
                <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  Запись недоступна
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SortableHead = ({ label, column, sortConfig, onSort }) => {
  const isActive = sortConfig.key === column;
  return (
    <TableHead className="cursor-pointer select-none" onClick={() => onSort(column)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />) : null}
      </span>
    </TableHead>
  );
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const formatChecklistScore = (value) => {
  if (value === null || value === undefined) return '—';
  const parsed = Number(value);
  return Number.isNaN(parsed) ? '—' : parsed.toFixed(1);
};

const formatDirection = (value) => {
  if (!value) return '—';
  const normalized = value.trim().toLowerCase();
  if (normalized === 'incoming') return 'Входящий';
  if (normalized === 'outgoing') return 'Исходящий';
  if (normalized === 'internal') return 'Внутренний';
  return value;
};

const formatClassificationType = (value) => {
  if (!value) return '—';
  return CALL_TYPE_LABELS[value] ?? value;
};

// Каноничное определение жалобы (согласовано с вкладкой «Жалобы» / backend ComplaintCallPredicate):
// тип delivery_issue ИЛИ есть категории негатива.
const isComplaint = (call) =>
  call?.callClassificationType === 'delivery_issue' ||
  (Array.isArray(call?.negativeCategories) && call.negativeCategories.length > 0);

const formatComplaints = (call) => {
  const categories = Array.isArray(call?.negativeCategories) ? call.negativeCategories : [];
  const subCategories = Array.isArray(call?.negativeSubCategories) ? call.negativeSubCategories : [];
  const all = [...categories, ...subCategories].filter(Boolean);
  if (!all.length) {
    // Жалоба без категорий негатива (delivery_issue) — подозрение.
    return isComplaint(call) ? 'Подозрение' : '—';
  }

  return all.join(', ');
};

const formatDetailsSubtitle = (call) => {
  if (!call) return '';
  const parts = [
    formatDate(call.callStartedAt),
    call.employeeName || '—',
    formatDuration(call.durationSeconds)
  ].filter(Boolean);
  return parts.join(' • ');
};

const InfoBlock = ({ label, value }) => (
  <div className="rounded-md border bg-card p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-sm font-medium text-foreground mt-1">{value ?? '—'}</div>
  </div>
);

const resolveRecordingUrl = (call) => {
  if (!call) return null;
  const trimmed = call.recordingUrl?.trim();
  if (trimmed?.startsWith('http')) return trimmed;
  return getTaskAudioUrl(call.callId);
};

export default CallDetails;
